import React, { useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { LanguageEnum } from "../../../../models/Language";
import translate from "../../../../services/Translate";
import { submitSingleInvitation } from "./InvitationModal.function";

const InvitationModal = ({ show, onClose }: { show: boolean; onClose: () => void }) => {
  const { language, showAlert, popAlert } = useSession();

  const [loading, setLoading] = useState(false);

  const initialFormState = {
    first_name: "",
    last_name: "",
    email: "",
    language: "",
    country: "",
    cohort: null as any,
    dt_start: "",
    dt_end: "",
    is_vip: false,
    invite_to_forum: true,
  };

  const [form, setForm] = useState(initialFormState);

  const _updateField = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const _submit = () => {
    onClose();

    submitSingleInvitation(
      {
        cohort_id: form.cohort.id,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email.toLowerCase(),
        language: form.language as LanguageEnum,
        dt_start: form.dt_start || undefined,
        dt_end: form.dt_end || undefined,
        is_vip: form.is_vip,
        invite_to_forum: form.invite_to_forum,
      },
      {
        setLoading,
        showAlert,
        popAlert,
        language,
        onSuccess: () => {
          setForm(initialFormState);
        },
      }
    );
  };

  const _isFormValid = form.first_name && form.last_name && form.email && form.cohort && form.language;

  return (
    <Popup
      show={show}
      onClose={onClose}
      modalTitle={translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.TITLE")}
      footerContent={
        <React.Fragment>
          <DstButton variant="light" value={translate(language, "GLOBAL.CANCEL")} clickFunction={onClose} />
          <DstButton
            variant="primary"
            loading={loading}
            disabled={!_isFormValid}
            value={translate(language, "GLOBAL.CONFIRM")}
            clickFunction={_submit}
          />
        </React.Fragment>
      }
    >
      <Form>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.FIRST_NAME")} />
            </Form.Label>
            <Form.Control
              value={form.first_name}
              onChange={(event) => _updateField("first_name", event.target.value)}
            />
          </Col>

          <Col md={6}>
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.LAST_NAME")} />
            </Form.Label>
            <Form.Control value={form.last_name} onChange={(event) => _updateField("last_name", event.target.value)} />
          </Col>

          <Col md={12}>
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.EMAIL")} />
            </Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={(event) => _updateField("email", event.target.value)}
            />
          </Col>

          <Col md={12}>
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.COHORT")} />
            </Form.Label>

            <MultiSelectLiveDropdown
              category={["cohorts"]}
              label={form.cohort?.name || translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.SELECT_COHORT")}
              language={language}
              toggleClass="w-100 border"
              isSingleSelect
              initialSelectedItems={form.cohort ? [{ value: form.cohort, category: "cohorts" }] : []}
              onConfirm={(selectedItems: any[]) => {
                _updateField("cohort", selectedItems[0]?.value || null);
              }}
            />
          </Col>

          <Col md={6}>
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
            </Form.Label>

            <Form.Select
              value={form.language}
              onChange={(event) => _updateField("language", event.target.value as LanguageEnum)}
            >
              <option value="" disabled>
                {translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.SELECT_LANGUAGE")}
              </option>

              {Object.values(LanguageEnum).map((lang) => (
                <option key={lang} value={lang}>
                  {lang === LanguageEnum.EN && translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.LANGUAGE_EN")}
                  {lang === LanguageEnum.FR && translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.LANGUAGE_FR")}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>{translate(language, "GLOBAL.START_DATE")}</Form.Label>
            <Form.Control
              type="date"
              value={form.dt_start}
              onChange={(event) => _updateField("dt_start", event.target.value)}
            />
            {!form.dt_start && (
              <Form.Text muted> {translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.START_DATE_HELP")}</Form.Text>
            )}
          </Col>

          <Col md={3}>
            <Form.Label>{translate(language, "GLOBAL.END_DATE")}</Form.Label>
            <Form.Control
              type="date"
              value={form.dt_end}
              onChange={(event) => _updateField("dt_end", event.target.value)}
            />
            {!form.dt_end && (
              <Form.Text muted>{translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.END_DATE_HELP")}</Form.Text>
            )}
          </Col>

          <Col md={6}>
            <Form.Check
              type="checkbox"
              label={translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.IS_VIP")}
              checked={form.is_vip}
              onChange={(event) => _updateField("is_vip", event.target.checked)}
            />
          </Col>

          <Col md={6}>
            <Form.Check
              type="checkbox"
              label={translate(language, "PAGES.INVITATIONS.INVITATION_MODAL.INVITE_TO_FORUM")}
              checked={form.invite_to_forum}
              onChange={(event) => _updateField("invite_to_forum", event.target.checked)}
            />
          </Col>
        </Row>
      </Form>
    </Popup>
  );
};

export default InvitationModal;
